import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Icon } from '@codex-brightworld/icons';

const isValueNonEmpty = value => typeof value === 'number' || !!value; // this method is defined here to allow 0 as valid value

const Container = styled.div`
  position: relative;
  margin: 0;
  padding: 0;
  border: none;
  font-size: 14px;
  font-family: ${({ theme }) => theme.fonts.default.fontFamily};
`;

const InputElement = styled.input.attrs(props => ({ 'data-qa': props.qa }))`
  border-radius: 6px;
  box-shadow: none;
  outline: none;
  width: 100%;
  margin: 0;
  font-size: 14px;
  font-family: ${({ theme }) => theme.fonts.default.fontFamily};
  height: 48px;

  ${({
    theme, value, valid, disabled, interactivePlaceholder
  }) => css`
    color: ${disabled ? theme.colors.neutral.grey[2] : theme.colors.neutral.currysBlack};
    padding: 15px;
    background-color: ${theme.colors.neutral.white};
    border: 1px solid ${theme.colors.neutral.grey[2]};
    &&& {
      ${!valid && `border-color: ${theme.colors.semantic.red}`};
    }
    ${(isValueNonEmpty(value) && interactivePlaceholder) && 'padding-bottom: 6px'};

    &:hover{
      outline: none;
      box-shadow: 0 0 6px 0 rgba(33, 48, 56, 0.2);
      border: 2px solid ${theme.colors.neutral.grey[2]};
      padding-top: 14px;
      padding-right: 14px;
      padding-left: 14px;
    };

    &:focus {
      border: 2px solid ${theme.colors.primary.purple};
      padding-top: 14px;
      padding-left: 14px;
      padding-right: 14px;
      ${(!isValueNonEmpty(value) && interactivePlaceholder) && 'padding-bottom: 6x'};
    };
  `};
`;

const interactivePlaceholderActiveStyle = css`
  top: 6px;
  left: 16px;
  height: 14px;
  font-size: ${props => props.interactiveFontSize};
  transition: all 0.2s ease-in-out;
`;

const InteractivePlaceholder = styled.div`
  transition: all 0.2s ease-in-out;
  pointer-events: none;
  font-size: 14px;
  position: absolute;
  top: 0;
  left: 0;
  height: 48px;
  align-items: center;
  display: ${({ value }) => (!isValueNonEmpty(value) ? 'flex' : 'none')};
  font-family: ${({ theme }) => theme.fonts.default.fontFamily};
  white-space: nowrap;
  ${({ theme, disabled }) => css`
    color: ${disabled ? theme.colors.neutral.grey[2] : theme.colors.neutral.grey[4]};
  `};

  ${({ theme, value, interactivePlaceholder }) => css`
    left: ${theme.spacing[4]};

    ${(isValueNonEmpty(value) && !interactivePlaceholder) ? css`display: none` : css`display: flex`};
    ${(isValueNonEmpty(value) && interactivePlaceholder) && interactivePlaceholderActiveStyle};

    ${InputElement}:focus ~ div {
      ${(!isValueNonEmpty(value) && interactivePlaceholder) && interactivePlaceholderActiveStyle};
      ${!interactivePlaceholder ? css`display: none` : css`display: flex`}
    }

  `}
`;

const ClearIcon = styled(Icon)`
  position: absolute;
  right: 5px;
  top: 10px;
`;

const ValidationMessage = styled.div`
  margin: 5px 0;
  padding-left: 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.semantic.red};
  transition: color 0.2s;
`;

// onClear attributed caused console warning when passed to <input> element
const Input = ({ onClear, ...props }) => (
  <Container
    className={props.className}
    {...props.attrs.container}
  >
    {props.renderInput(InputElement, props)}
    {props.renderPlaceholder(InteractivePlaceholder, props)}

    {props.clearIcon && isValueNonEmpty(props.value) &&
      <ClearIcon icon="close" size="large" onClick={e => onClear(e)} />}
    {props.validationMessage && !props.valid &&
      <ValidationMessage>{props.validationMessage}</ValidationMessage>}
  </Container>
);

Input.propTypes = {
  attrs: PropTypes.shape({
    container: PropTypes.shape({}),
    input: PropTypes.shape({})
  }),
  analyticsAttributeName: PropTypes.string,
  analyticsAttributeValue: PropTypes.string,
  autoComplete: PropTypes.string,
  className: PropTypes.string,
  clearIcon: PropTypes.bool,
  disabled: PropTypes.bool,
  inputElementRef: PropTypes.shape({}),
  interactivePlaceholder: PropTypes.bool,
  name: PropTypes.string,
  placeholderText: PropTypes.string,
  readOnly: PropTypes.bool,
  validationMessage: PropTypes.string,
  type: PropTypes.oneOf(['text', 'password', 'number', 'email']),
  valid: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  qa: PropTypes.string,
  fontSize: PropTypes.string,
  interactiveFontSize: PropTypes.string,
  /**
   * Renders input component.
   * @param DefaultInput
   * @param attrs
   * @param type
   * @param value
   * @param analyticsAttributeName
   * @param analyticsAttributeValue
   * @param autoComplete
   * @param disabled
   * @param onBlur
   * @param onChange
   * @param onFocus
   * @param onKeyDown
   * @param valid
   * @param name
   * @param readOnly
   * @param inputElementRef
   * @param fontSize
   * @returns {*}
   */
  renderInput: PropTypes.func,

  /**
   * Renders placeholder wrapper, which is responsible for defining
   * the interactive placeholder behaviour.
   * @param DefaultPlaceholderWrapper
   * @param interactivePlaceholder
   * @param placeholderText
   * @param renderPlaceholder
   * @param value
   * @returns {*}
   */
  renderPlaceholder: PropTypes.func
};

Input.defaultProps = {
  attrs: {
    container: {},
    input: {}
  },
  analyticsAttributeName: 'data-interaction',
  analyticsAttributeValue: undefined,
  autoComplete: undefined,
  className: '',
  clearIcon: false,
  disabled: false,
  inputElementRef: null,
  interactivePlaceholder: false,
  name: null,
  placeholderText: '',
  readOnly: false,
  validationMessage: '',
  type: 'text',
  valid: true,
  value: '',
  onBlur: null,
  onChange: null,
  onClear: null,
  onFocus: null,
  onKeyDown: null,
  qa: undefined,
  fontSize: '13px',
  interactiveFontSize: '12px',
  renderInput: (DefaultInput, {
    attrs, type, value, autoComplete, disabled, name, onBlur, onChange, onFocus, onKeyDown, valid,
    readOnly, analyticsAttributeName, analyticsAttributeValue, inputElementRef, fontSize, interactivePlaceholder
  }) => (
    <DefaultInput
      autoComplete={autoComplete}
      disabled={disabled}
      type={type}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      ref={inputElementRef}
      valid={valid}
      name={name}
      readOnly={readOnly}
      fontSize={fontSize}
      interactivePlaceholder={interactivePlaceholder}
      {...attrs.input}
      {...(analyticsAttributeValue ? { [analyticsAttributeName]: analyticsAttributeValue } : {})}
    />
  ),
  renderPlaceholder: (DefaultPlaceholder, {
    interactivePlaceholder, placeholderText, disabled, value, fontSize, interactiveFontSize
  }) => (
    <DefaultPlaceholder
      interactivePlaceholder={interactivePlaceholder}
      value={value}
      fontSize={fontSize}
      interactiveFontSize={interactiveFontSize}
      disabled={disabled}
    >
      {placeholderText}
    </DefaultPlaceholder>
  )
};

/** @component */
export default Input;
